import React, { useState } from 'react';
import { HiOutlineExclamationTriangle, HiOutlineArrowTopRightOnSquare, HiOutlineClipboard, HiOutlineCheck, HiOutlineEllipsisHorizontal } from 'react-icons/hi2';
import { theme } from '../theme';

// Detecta WebViews de apps sociais que quebram POSTs / OAuth
export function detectInAppBrowser() {
  if (typeof navigator === 'undefined') return null;
  const ua = navigator.userAgent || '';
  if (/Instagram/i.test(ua)) return 'Instagram';
  if (/FBAN|FBAV|FB_IAB|FBIOS/i.test(ua)) return 'Facebook';
  if (/MessengerLite|\bMessenger\b/i.test(ua)) return 'Messenger';
  if (/TikTok|BytedanceWebview|musical_ly/i.test(ua)) return 'TikTok';
  if (/Line\//i.test(ua)) return 'LINE';
  if (/MicroMessenger/i.test(ua)) return 'WeChat';
  if (/KAKAOTALK/i.test(ua)) return 'KakaoTalk';
  if (/LinkedInApp/i.test(ua)) return 'LinkedIn';
  if (/Snapchat/i.test(ua)) return 'Snapchat';
  return null;
}

function detectPlatform() {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent || '';
  if (/Android/i.test(ua)) return 'android';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  return 'other';
}

// Constroi intent:// que forca abrir no Chrome (Android)
function buildAndroidIntent(fullUrl) {
  try {
    const u = new URL(fullUrl);
    const path = u.pathname + u.search + u.hash;
    const fallback = encodeURIComponent(fullUrl);
    return `intent://${u.host}${path}#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${fallback};end`;
  } catch {
    return fullUrl;
  }
}

// Tenta abrir googlechrome:// (iOS). Se Chrome nao existe, cai no fallback (copiar).
function buildIosChromeUrl(fullUrl) {
  try {
    const u = new URL(fullUrl);
    const scheme = u.protocol === 'https:' ? 'googlechromes' : 'googlechrome';
    return `${scheme}://${u.host}${u.pathname}${u.search}${u.hash}`;
  } catch {
    return fullUrl;
  }
}

export default function InAppBrowserWarning({ appName }) {
  const [copied, setCopied] = useState(false);
  const url = typeof window !== 'undefined' ? window.location.href : '';
  const platform = detectPlatform();

  const abrirChromeAndroid = () => {
    window.location.href = buildAndroidIntent(url);
  };

  const abrirChromeIos = () => {
    // Tenta Chrome — se nao abrir em 1.5s, provavelmente nao esta instalado
    const t = Date.now();
    window.location.href = buildIosChromeUrl(url);
    setTimeout(() => {
      if (Date.now() - t < 2000 && document.visibilityState === 'visible') {
        // Nao mudou de app — provavel que Chrome nao existe. Faz copy.
        copiarLink();
      }
    }, 1500);
  };

  const copiarLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch {
      // Fallback: prompt
      window.prompt('Copie o link:', url);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.iconWrap}>
          <HiOutlineExclamationTriangle size={38} color={theme.colors.primary} />
        </div>

        <h1 style={styles.title}>Abra no seu navegador</h1>
        <p style={styles.subtitle}>
          Você está no navegador do <strong>{appName}</strong>, que bloqueia
          alguns recursos necessários para criar sua conta com segurança.
        </p>

        <div style={styles.actions}>
          {platform === 'android' && (
            <button onClick={abrirChromeAndroid} style={styles.btnPrimary}>
              <HiOutlineArrowTopRightOnSquare size={18} />
              Abrir no Chrome
            </button>
          )}
          {platform === 'ios' && (
            <>
              <button onClick={abrirChromeIos} style={styles.btnPrimary}>
                <HiOutlineArrowTopRightOnSquare size={18} />
                Abrir no Chrome
              </button>
              <button onClick={copiarLink} style={styles.btnSecondary}>
                {copied ? <HiOutlineCheck size={18} /> : <HiOutlineClipboard size={18} />}
                {copied ? 'Link copiado!' : 'Copiar link'}
              </button>
            </>
          )}
          {platform === 'other' && (
            <button onClick={copiarLink} style={styles.btnPrimary}>
              {copied ? <HiOutlineCheck size={18} /> : <HiOutlineClipboard size={18} />}
              {copied ? 'Link copiado!' : 'Copiar link'}
            </button>
          )}
        </div>

        <div style={styles.divider}>
          <span style={styles.dividerLine} />
          <span style={styles.dividerLabel}>ou</span>
          <span style={styles.dividerLine} />
        </div>

        <div style={styles.manualStep}>
          <div style={styles.stepIconWrap}>
            <HiOutlineEllipsisHorizontal size={20} />
          </div>
          <p style={styles.stepText}>
            Toque nos <strong>três pontos</strong> no canto da tela e escolha
            <strong> "Abrir no navegador"</strong> ou <strong>"Abrir no Chrome"</strong>.
          </p>
        </div>

        <p style={styles.url}>{url}</p>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(155deg, #FDF7F4 0%, #F5E6E3 50%, #E8B4A0 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: theme.font, padding: 20, position: 'relative', overflow: 'hidden',
  },
  card: {
    background: 'rgba(255,255,255,0.98)', backdropFilter: 'blur(20px)',
    borderRadius: 24, padding: '36px 28px', width: '100%', maxWidth: 440,
    textAlign: 'center', boxShadow: '0 20px 60px rgba(61,44,44,0.15)',
    border: '1px solid rgba(255,255,255,0.6)',
  },
  iconWrap: {
    width: 72, height: 72, borderRadius: '50%',
    background: theme.colors.primaryLight,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    margin: '0 auto 20px',
  },
  title: {
    fontSize: 22, fontWeight: 700, color: theme.colors.text,
    margin: '0 0 10px', fontFamily: theme.font,
  },
  subtitle: {
    fontSize: 14, color: theme.colors.textSecondary, lineHeight: 1.55,
    margin: '0 0 24px',
  },
  actions: {
    display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20,
  },
  btnPrimary: {
    width: '100%', padding: '14px 16px', fontSize: 15, fontWeight: 600,
    background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.primaryDark})`,
    color: '#fff', border: 'none', borderRadius: 12, cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(196,120,110,0.3)', fontFamily: theme.font,
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  },
  btnSecondary: {
    width: '100%', padding: '13px 16px', fontSize: 14, fontWeight: 600,
    background: '#fff', color: theme.colors.text,
    border: `1.5px solid ${theme.colors.border}`, borderRadius: 12,
    cursor: 'pointer', fontFamily: theme.font,
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  },
  divider: {
    display: 'flex', alignItems: 'center', gap: 12,
    margin: '4px 0 20px', color: theme.colors.textMuted, fontSize: 12,
  },
  dividerLine: { flex: 1, height: 1, background: theme.colors.border },
  dividerLabel: { whiteSpace: 'nowrap' },
  manualStep: {
    display: 'flex', alignItems: 'flex-start', gap: 12,
    background: theme.colors.bgSection,
    borderRadius: 12, padding: '14px 16px', textAlign: 'left',
    border: `1px solid ${theme.colors.border}`,
  },
  stepIconWrap: {
    width: 32, height: 32, borderRadius: 8,
    background: '#fff', border: `1px solid ${theme.colors.border}`,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: theme.colors.textSecondary, flexShrink: 0,
  },
  stepText: {
    margin: 0, fontSize: 13, color: theme.colors.textSecondary, lineHeight: 1.5,
  },
  url: {
    margin: '20px 0 0', fontSize: 11, color: theme.colors.textMuted,
    wordBreak: 'break-all', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    background: theme.colors.bgSection, padding: '8px 12px', borderRadius: 8,
  },
};
